import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { routes } from './app.routes';
import { RuntimeBootstrapService } from './services/runtime-bootstrap.service';
import { SilenceNg0100ErrorHandler } from './silence-ng0100.error-handler';
import { getZavoBridge } from './services/mcp/zavo-bridge';

import { initializeApp, provideFirebaseApp, getApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore, } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { initializeAuth, indexedDBLocalPersistence, inMemoryPersistence, Persistence } from 'firebase/auth';

import { environment } from '../environments/environment';
import { provideIonicAngular } from '@ionic/angular/standalone';

/**
 * Desktop app (Electron) uses in-memory persistence so the builder signs in
 * each time the app opens. The Electron preload script exposes
 * `window.zavoBridge` via contextBridge before the page loads, so this check
 * is safe at bootstrap time. Browsers keep indexedDB persistence as before.
 */
export function chooseAuthPersistence(): Persistence {
  const isDesktop = !!getZavoBridge();
  return isDesktop ? inMemoryPersistence : indexedDBLocalPersistence;
}

export const appConfig: ApplicationConfig = {
  providers: [provideZoneChangeDetection({ eventCoalescing: true }), provideRouter(routes),
    // Angular animations, loaded lazily (Bastion stage 5f). Bastion's triggers (dashboard/bastion/ui/animations.ts)
    // are the only ones in the app, but this is NOT fully app-neutral: once the animations chunk has loaded (the first
    // Bastion screen), every view in the app renders through the AnimationRenderer, and an element removed anywhere
    // leaves on the animation engine's flush at the end of the render pass, not at the moment it is removed.
    provideAnimationsAsync(),
    // Drop ONLY the dev-mode NG0100 (ExpressionChangedAfterItHasBeenChecked) console noise — harmless,
    // never in prod; all other errors still log. See silence-ng0100.error-handler.ts.
    { provide: ErrorHandler, useClass: SilenceNg0100ErrorHandler },
    // The artifact bootstrap (Track 4): load per-project /zavo-runtime.json before the app starts so
    // login/signup scope to the right Identity Platform pool. No-op in the editor/dev build.
    provideAppInitializer(() => inject(RuntimeBootstrapService).load()),
    provideFirebaseApp(() =>
    initializeApp(environment.firebaseConfig)),
    // provideAuth(() => getAuth()), 
  provideAuth(() => {
      const auth = initializeAuth(getApp(), {
        persistence: chooseAuthPersistence(),
      });
      // Identity Platform (Track E) — RESTORE the auth-pool scope BEFORE the SDK reloads the
      // persisted user. `auth.tenantId` is an in-memory property: login sets it (signInWithEmail),
      // but a hard refresh starts it at null. The SDK then reloads the tenant-minted session in the
      // DEFAULT (agent) pool context — GCIP rejects the token, the SDK silently signs the user out
      // (reloadAndSetCurrentUserOrClear → directlySetCurrentUser(null)), and the app falls to the
      // system landing page ("logged in, refresh, bounced to landing; clearing IndexedDB fixes it").
      // initializeAuth only QUEUES the restore on a microtask, so setting tenantId synchronously here
      // wins the race. Same pool login.component computes: project pool at runtime, editor pool in the
      // editor. undefined (default/pre-migration pool) → leave null, which already matched.
      try {
        const pool = environment.isRuntime
          ? (window as any)?.ZAVO_RUNTIME?.authPoolId
          : (environment as any).editorPoolId;
        if (pool) (auth as any).tenantId = pool;
      } catch { /* window/config unavailable — fall back to default-pool behavior */ }
      return auth;
    }),
    provideFirestore(() => getFirestore()), 
    provideFunctions(() => getFunctions(getApp(), 'asia-south1')),
    provideStorage(() => getStorage()), provideIonicAngular({}),
]
};